/**
 * チェス盤 (8x8) コンポーネント。
 *
 * - 駒を click → from 選択 → to 候補ハイライト → click で着手。
 * - プロモーション選択は専用ダイアログ。
 * - キャスリングの to は king の最終位置 (g/c file)。 UI 上は king 駒を click → 反対側のマス click。
 */

import { useState } from 'react';
import {
  CHESS_SIZE,
  type ChessMove,
  type ChessState,
  isCastle,
  isPromotion,
  legalTargetsFor,
  moveTo,
  type PieceKind,
  type Side,
} from '../../games/chess';
import { BoardCoords } from './BoardCoords';
import { ChessPieceGlyph } from './ChessPieceGlyph';
import './OwnFrontRim.css';

interface Props {
  state: ChessState;
  legal?: ChessMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (mv: ChessMove) => void;
  mini?: boolean;
  lastMove?: ChessMove;
  /** Replay / Archive / チュートリアル等の静的ビューでは「相手の手番」 表示を出さない。 */
  staticView?: boolean;
  /** チュートリアル用の強調マス (該当セルに `tutorial-hint` クラスが付く)。 */
  tutorialHighlight?: [number, number][];
}


export function ChessBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  tutorialHighlight,
}: Props) {
  const isMyTurn =
    myTurnAllowed !== undefined ? myTurnAllowed : yourSide != null && yourSide === state.side;
  // 「相手の手番」 hint: Reversi/Animal と同仕様。 mini / staticView (Replay/Tutorial) では出さない。
  const showOppHint = !mini && !staticView && !isMyTurn;
  const [selected, setSelected] = useState<[number, number] | null>(null);
  const [pendingPromo, setPendingPromo] = useState<ChessMove[] | null>(null);

  const targets = selected ? legalTargetsFor(legal ?? [], selected) : [];

  // 表示は自分側が下に来るよう row/col を並び替える。
  // - First (白) 視点: row 7 (黒陣) が画面上、 row 0 (白陣) が画面下。 col は a..h (=0..7) 左→右。
  // - Second (黒) 視点: row 0 が画面上、 row 7 (黒陣) が画面下。 col も h..a (=7..0) で反転 (= 黒の king が右に来る)。
  // - 観戦 (yourSide なし) は white 視点 (デフォ)。
  const flip = yourSide === 'Second';
  const rows: number[] = flip
    ? Array.from({ length: CHESS_SIZE }, (_, i) => i)
    : Array.from({ length: CHESS_SIZE }, (_, i) => CHESS_SIZE - 1 - i);
  const cols: number[] = flip
    ? Array.from({ length: CHESS_SIZE }, (_, i) => CHESS_SIZE - 1 - i)
    : Array.from({ length: CHESS_SIZE }, (_, i) => i);

  const handleCellClick = (r: number, c: number) => {
    if (mini || !isMyTurn) return;
    const cell = state.board[r][c];
    // 1. 既選択がない、 もしくは別の自駒を再選択
    if (!selected) {
      if (cell && cell.owner === state.side) {
        setSelected([r, c]);
      }
      return;
    }
    if (cell && cell.owner === state.side) {
      // 自駒上でも、 king→castle to のときは目標として扱う。
      const cands = targets.filter((m) => {
        const t = moveTo(m);
        if (t) return t[0] === r && t[1] === c;
        if (isCastle(m)) {
          // キャスリング: king の最終 column (kingside=6, queenside=2)、 row はキング位置と同じ。
          const kingRow = m.Castle.side === 'First' ? 0 : 7;
          const kingCol = m.Castle.kingside ? 6 : 2;
          return r === kingRow && c === kingCol;
        }
        return false;
      });
      if (cands.length === 0) {
        setSelected([r, c]);
        return;
      }
    }
    // 2. ターゲット候補のうち (r, c) を to に持つ手
    const cands = targets.filter((m) => {
      const t = moveTo(m);
      if (t) return t[0] === r && t[1] === c;
      return false;
    });
    // キャスリングは別判定: king を選んでいて、 to が g/c file。
    const castles = (legal ?? []).filter((m) => {
      if (!isCastle(m)) return false;
      const kingRow = m.Castle.side === 'First' ? 0 : 7;
      const kingCol = m.Castle.kingside ? 6 : 2;
      // king の元位置: (kingRow, 4)。 selected がそれで、 click 先が g/c file 同じ row なら castle。
      return selected[0] === kingRow && selected[1] === 4 && r === kingRow && c === kingCol;
    });
    const all = [...cands, ...castles];
    if (all.length === 0) {
      setSelected(null);
      return;
    }
    // プロモーション含む?
    const promos = all.filter(isPromotion);
    if (promos.length > 0) {
      setPendingPromo(promos);
      return;
    }
    onMove?.(all[0]);
    setSelected(null);
  };

  const lastFrom = lastMove
    ? (() => {
        if (isPromotion(lastMove)) return lastMove.Promotion.from;
        if (isCastle(lastMove)) return null;
        if ('Normal' in lastMove) return lastMove.Normal.from;
        if ('EnPassant' in lastMove) return lastMove.EnPassant.from;
        return null;
      })()
    : null;
  const lastTo = lastMove ? moveTo(lastMove) : null;

  return (
    <div className={`chess-frame ${mini ? 'mini' : ''} ${showOppHint ? 'not-my-turn' : ''}`}>
      <div className="chess-board" data-own-rim={!mini && (yourSide === 'First' || yourSide === 'Second') ? yourSide === 'First' ? 'white' : 'black' : undefined}>
        {/* 座標は升の外 (枠) に。 表示順のまま渡すので、 後手視点の反転にも追従する。 */}
        {!mini && (
          <BoardCoords
            files={cols.map((c) => String.fromCharCode(97 + c))}
            ranks={rows.map((r) => `${r + 1}`)}
          />
        )}
        {rows.flatMap((r) =>
          cols.map((c) => {
            const cell = state.board[r][c];
            // a1 は暗色、 h1 は明色 (各プレイヤーから見て右手前が白升) が標準。
            // r はランク index (rankLabel = r+1)、 c はファイル index (a=0)。
            const isLight = (r + c) % 2 === 1;
            const isSelected = selected != null && selected[0] === r && selected[1] === c;
            const isTarget =
              targets.some((m) => {
                const t = moveTo(m);
                return t != null && t[0] === r && t[1] === c;
              }) ||
              // キャスリング目標
              (selected != null &&
                selected[1] === 4 &&
                (legal ?? []).some(
                  (m) =>
                    isCastle(m) &&
                    ((m.Castle.kingside && r === selected[0] && c === 6) ||
                      (!m.Castle.kingside && r === selected[0] && c === 2)),
                ));
            const isLast =
              !mini &&
              ((lastFrom != null && lastFrom[0] === r && lastFrom[1] === c) ||
                (lastTo != null && lastTo[0] === r && lastTo[1] === c));
            const isTutorialHint =
              tutorialHighlight?.some(([hr, hc]) => hr === r && hc === c) ?? false;
            const rankLabel = `${r + 1}`;
            const fileLabel = String.fromCharCode('a'.charCodeAt(0) + c);
            return (
              <button
                key={`${r}-${c}`}
                className={`cell ${isLight ? 'light' : 'dark'} ${
                  isSelected ? 'selected' : ''
                } ${isTarget ? 'target' : ''} ${isLast ? 'last-move' : ''} ${
                  isTutorialHint ? 'tutorial-hint' : ''
                }`}
                disabled={mini}
                onClick={() => handleCellClick(r, c)}
                aria-label={`${fileLabel}${rankLabel}`}
              >
                {cell && (
                  <span className={`chess-piece ${cell.owner === 'First' ? 'white' : 'black'}`}>
                    <ChessPieceGlyph kind={cell.kind} owner={cell.owner} />
                  </span>
                )}
              </button>
            );
          }),
        )}
      </div>
      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
      {pendingPromo && (
        <div className="chess-promo-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="chess-promo-dialog" data-testid="chess-promo" role="dialog" aria-label="プロモーション">
            <div className="chess-promo-title">プロモーション</div>
            <div className="chess-promo-buttons">
              {/* 文字ではなく駒の形で選ぶ (盤上の駒と同じグリフ)。 名前は読み上げ用。 */}
              {(['Queen', 'Rook', 'Bishop', 'Knight'] as PieceKind[]).map((kind) => {
                const mv = pendingPromo.find((m) => isPromotion(m) && m.Promotion.promote_to === kind);
                if (!mv) return null;
                const owner = state.side;
                return (
                  <button
                    key={kind}
                    type="button"
                    className="chess-promo-btn"
                    aria-label={kind}
                    onClick={() => {
                      onMove?.(mv);
                      setPendingPromo(null);
                      setSelected(null);
                    }}
                  >
                    <ChessPieceGlyph kind={kind} owner={owner} size={44} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
